import type {AxiosError} from 'axios'
import {toast} from 'react-toastify'

const DEFAULT_FORBIDDEN = 'You do not have permission to perform this action.'
const DEFAULT_NETWORK_ERROR = 'Unable to connect to the server. Please check your network connection.'
const DEFAULT_FALLBACK_ERROR = 'An unexpected error occurred. Please try again.'

export type PermissionHint = 'load' | 'create' | 'update' | 'delete' | 'save' | 'general'

const STACK_LIKE = /(exception|trace|sql|hibernate|org\.springframework|java\.|nullpointer|syntaxerror)/i

function permissionHintMessage(hint: PermissionHint | undefined): string {
  switch (hint) {
    case 'load':
      return 'You do not have permission to view this data.'
    case 'create':
      return 'You do not have permission to create this record.'
    case 'update':
      return 'You do not have permission to edit this record.'
    case 'delete':
      return 'You do not have permission to delete this record.'
    case 'save':
      return 'You do not have permission to save this record.'
    default:
      return DEFAULT_FORBIDDEN
  }
}

/**
 * Checks whether an error represents a network failure (no response received from server).
 */
export function isNetworkError(err: unknown): boolean {
  const ae = err as AxiosError
  return Boolean(ae && !ae.response && ae.request)
}

/**
 * Checks whether an error is an authentication / authorization rejection (401 / 403).
 */
export function isPermissionError(err: unknown): boolean {
  const ae = err as AxiosError
  const status = ae?.response?.status
  return status === 401 || status === 403
}

/**
 * Extracts a clean, human-readable error message from any backend API error,
 * Axios error, JavaScript Error, or unknown object.
 */
export function getApiErrorMessage(
  err: unknown,
  fallback: string = DEFAULT_FALLBACK_ERROR,
  permissionHint: PermissionHint = 'general'
): string {
  if (!err) return fallback

  // 1. Direct string error
  if (typeof err === 'string') {
    const trimmed = err.trim()
    return trimmed && !STACK_LIKE.test(trimmed) ? trimmed : fallback
  }

  const ae = err as AxiosError<{
    message?: string
    error?: string
    fieldErrors?: Record<string, string>
    status?: number
  }>

  // 2. Network error (server unreachable / timeout / DNS failure)
  if (isNetworkError(err)) {
    return DEFAULT_NETWORK_ERROR
  }

  // 3. Axios HTTP error response
  if (ae?.response) {
    const status = ae.response.status
    const data: any = ae.response.data

    // Handle 401/403 Permission Rejections
    if (status === 401 || status === 403) {
      const backendMsg =
        typeof data?.message === 'string' && !STACK_LIKE.test(data.message)
          ? data.message.trim()
          : ''
      if (backendMsg && backendMsg.length <= 140) {
        return `${permissionHintMessage(permissionHint)} ${backendMsg}`
      }
      return permissionHintMessage(permissionHint)
    }

    // Handle Backend data message / error string
    if (typeof data === 'string') {
      const trimmed = data.trim()
      if (trimmed && !STACK_LIKE.test(trimmed) && trimmed.length <= 250) {
        return trimmed
      }
    }

    // Handle Field Validation Errors map from backend
    if (data?.fieldErrors && typeof data.fieldErrors === 'object') {
      const messages = Object.values(data.fieldErrors).filter(Boolean)
      if (messages.length > 0) {
        return messages.join(', ')
      }
    }

    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message.trim()) {
        const msg = data.message.trim()
        if (!STACK_LIKE.test(msg)) {
          return msg
        }
      }
      if (typeof data.error === 'string' && data.error.trim()) {
        const errStr = data.error.trim()
        if (!STACK_LIKE.test(errStr) && errStr !== 'Internal Server Error') {
          return errStr
        }
      }
    }

    if (status === 404) {
      return 'The requested resource was not found.'
    }
    if (status === 409) {
      return 'The operation could not be completed due to a data conflict or active dependency.'
    }
    if (status === 413) {
      return 'The uploaded file is too large. Please upload a smaller file.'
    }
  }

  // 4. Standard JavaScript Error object
  if (err instanceof Error) {
    const msg = err.message ? err.message.trim() : ''
    if (msg && !STACK_LIKE.test(msg) && !msg.startsWith('Request failed with status code')) {
      return msg
    }
  }

  return fallback
}

/**
 * Backwards-compatible formatApiError using enhanced message extractor.
 */
export function formatApiError(
  err: unknown,
  fallback: string = DEFAULT_FALLBACK_ERROR,
  permissionHint: PermissionHint = 'general'
): string {
  return getApiErrorMessage(err, fallback, permissionHint)
}

/**
 * Helper to display a standardized error toast with automatic backend message extraction.
 */
export function showErrorToast(
  err: unknown,
  fallback: string = DEFAULT_FALLBACK_ERROR,
  permissionHint: PermissionHint = 'general'
): string {
  const message = getApiErrorMessage(err, fallback, permissionHint)
  toast.error(message)
  return message
}
